<template>
  <form
    :class="[
      'form',
      {
        'form--wide' : wide,
      },
    ]"
    @submit.prevent="submitForm"
  >
    <template
      v-for="(field, key) in fields"
    >
      <component
        v-if="(field.heading || {}).heading"

        :is="field.heading.tag || `h3`"

        :key="`fieldset-heading-${key}`"

        :class="[
          `form__field-heading`,
          field.heading.level || `h2`,
        ]"

        v-html="field.heading.heading"
      />
      <div v-if="field.tooltip" :key="`tooltip-${key}`">
        <a href="#" :class="[`expand-button-${key}`, `link`, `link--secondary`, `tdn`]">{{ field.tooltip.title }}</a>
        <Expander :trigger-element="`.expand-button-${key}`">
          <ui-tooltip
            level="tip"
            :close="false"
          >
            <template slot="message">
              <span v-html="field.tooltip.content" />
            </template>
          </ui-tooltip>
        </Expander>
      </div>
      <p
        v-if="(field.intro || {}).copy"

        :key="`fieldset-intro-${key}`"

        :class="[
          `form__field-intro`,
          field.intro.size || `p`,
          {
            'mt0': !!(field.heading || {}).heading,
          },
        ]"

        v-html="field.intro.copy"
      />
      <module-form-fieldset
        :key="`fieldset-${key}`"

        :form-id="formId"
        :field="field"
        :field-id="key"
        :server-error="serverErrors[key]"

        @fieldTouch="onFieldTouch(key)"
        @fieldReset="onFieldReset(key)"
      />
    </template>

    <p v-if="serverErrors._global" :class="`form__errors small`">{{ serverErrors._global }}</p>
    <slot name="before-submit"/>
    <div v-if="!noSubmit" :class="[hideSubmit ? 'visuallyhidden' : 'form__submit']">
      <module-button
        :class="[
          {
            'btn--hidden': hideSubmit,
          }
        ]"

        :button="button || ''"
        :size="buttonSize || ''"
        :disabled="$v.$invalid || invalid"
        :loading="status.loading"
        :success="status.success"
        type="submit"
        :dark="buttonDark"
      >
        {{ submit || `Submit` }}
      </module-button>
      <slot name="submit-extras" />
    </div>
  </form>
</template>

<script>
// Vuex
import { mapActions, mapGetters, mapMutations } from 'vuex';
// Services
import { arrayLoop } from 'services/array';
// Modules
import ModuleButton from 'modules/Button';
import ModuleFormFieldset from 'modules/form/Fieldset';
// UI
import ExpandButton from 'ui/ExpandButton';
import Expander from 'ui/Expander';
import UiTooltip from 'ui/Tooltip';
// Export
export default {
  name: `module-form`,
  // Components
  components: {
    ModuleButton,
    ModuleFormFieldset,
    Expander,
    UiTooltip,
    ExpandButton,
  },
  // Props
  props: {
    wide: Boolean,
    fields: Object,
    terms: Array,
    submit: String,
    hideSubmit: Boolean,
    noSubmit: Boolean,
    button: {
      type: String,
      default: `light`,
    },
    buttonSize: {
      type: String,
      default: `m`,
    },
    buttonDark: {
      type: Boolean,
      default: false,
    },
    formId: String,
    action: Function,
    success: Function,
    fail: Function,
    doNotReset: Boolean,
  },
  // Data
  computed: {
    ...mapGetters(`forms`, [
      `getModel`,
      `getStatus`,
      `getValidations`,
      `getServerErrors`,
    ]),
    model() {
      return this.getModel(this.formId);
    },
    status() {
      return this.getStatus(this.formId);
    },
    validations() {
      return this.getValidations(this.formId);
    },
    serverErrors() {
      return this.getServerErrors(this.formId);
    },
    invalid() {
      let invalid = false;
      // TODO: Replace with Array prototype
      Object.keys(this.serverErrors).forEach(key => {
        if (this.serverErrors[key]) {
          invalid = true;
        }
      });
      return invalid;
    },
  },
  watch: {
    fields: {
      handler(fields) {
        arrayLoop(fields, (key, field) => {
          this.$store.commit(`forms/updateFieldValidation`, { // TODO: mapMutations
            form: this.formId,
            key,
            field,
          });
        });
      },
      deep: true,
    },
  },
  // Methods
  methods: {
    ...mapActions(`forms`, {
      'formEvent': `event`,
      'init': `init`,
      'onSubmit': `onSubmit`,
      'setLoading': `setLoading`,
      'setSuccess': `setSuccess`,
      'unsetLoading': `unsetLoading`,
    }),
    ...mapMutations(`forms`, [
      `updateValid`,
    ]),
    onFieldTouch(id) {
      if (!this.$v.model[id]) return;

      this.$v.model[id].$touch();
    },
    onFieldReset(id) {
      if (!this.$v.model[id]) return;

      this.$v.model[id].$reset();
    },
    submitForm() {
      if (this.noSubmit) {
        return false;
      }
      this.setLoading(this.formId);
      const settle = () => {
        this.unsetLoading(this.formId);
      };
      const fail = (data = {}) => {
        const errors = data.errors || [];
        for (const error of errors) {
          this.formEvent({
            event: `serverError`,
            form: this.formId,
            field: error.field,
            error: error.message,
          });
        }
        this.fail ? (this.fail(errors) || null) : null; // eslint-disable-line no-unused-expressions
      };
      const success = (data) => {
        this.success ? (this.success(data) || null) : null;  // eslint-disable-line no-unused-expressions
        this.setSuccess(this.formId);
      };
      if (typeof(this.action) !== `function`) {
        return false;
      }

      this.action()
        .then((response = {}) => {
          settle();
          if (response.status > 300) {
            fail(response.data);
          } else {
            success(response.data);
          }
        })
        .catch((response = {}) => {
          settle();
          fail(response.data);
        });
    },
  },
  // Lifecycle
  created() {
    this.init({
      id: this.formId,
      fields: this.fields,
    });
    this.$watch(`$v`, {
      handler(val) {
        this.updateValid({
          form: this.formId,
          value: val.model,
        });
      },
      deep: true,
    });
  },
  beforeMount() {
    this.updateValid({
      form: this.formId,
      value: this.$v.model,
    });
  },
  destroyed() {
    if (this.doNotReset !== true) {
      this.$store.commit(`forms/resetForm`, this.formId); // TODO: make action and map
      // setTimeout(() => {
      // }, 500);
    }
  },

  // Validations
  validations() {
    return {
      model: this.validations,
    };
  },
};
</script>



// WEBPACK FOOTER //
// Form.vue?0b30f401